import styled from 'styled-components';
import { forwardRef } from 'react';
import { match } from 'ts-pattern';
import noop from 'utils/noop';
import { Goal } from 'modules/goals/types';
import { Body2, spacing, Subtitle1 } from 'ui/theme';
import { Button, BUTTON_SIZES, BUTTON_VARIANTS, TextButton } from 'ui/button';
import { PlaceholderState } from 'ui/indexPages/PlaceholderState';
import { FlexSpacer } from 'ui/flexSpacer';
import { ErrorStatePageError } from 'ui/illustrations/ErrorStatePageError';
import { colors } from 'ui/theme/colors';
import { AddNew24 } from 'ui/icons/aurora/24px/AddNew24';
import { EmptyStateExampleCard } from './EmptyStateExampleCard';
import { DashboardSectionContent } from '../Dashboard.styled';
import { GoalCard } from './GoalCard';
import { GoalCardLoading } from './GoalCard/GoalCardLoading';
import { AddGoalSelect } from './AddGoalSelect';
import { ALIGNMENT, SelectValueDisplayProps, SelectValueType } from 'ui/selects';
import { ModalType, useModalContext } from 'modules/missionControl/contexts/ModalContext';

const EXAMPLE_CARD_ID = 'empty-state-example-card';

export const StyledTextButton = styled(TextButton)`
  padding-inline: 0;
  font-size: inherit;
`;

export const GoalsDashboardLoadingState = () => (
  <DashboardSectionContent>
    <GoalCardLoading />
    <GoalCardLoading />
  </DashboardSectionContent>
);

export const GoalsDashboardErrorState = ({
  refetch,
}: {
  refetch: () => void;
}) => (
  <FlexSpacer justifyContent="center" alignItems="center" flexDirection="column" width="100%">
    <PlaceholderState
      Illustration={ErrorStatePageError}
      title="We’ve hit a snag"
      data-test="goals-error-state"
    >
      Sorry, we're having trouble loading goals for this project. Come back again later or{' '}
      <StyledTextButton onClick={refetch} size={BUTTON_SIZES.MEDIUM}>
        try reloading.
      </StyledTextButton>
    </PlaceholderState>
  </FlexSpacer>
);

const GoalsDashboardEmptyStateContent = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: ${spacing.x3};
  padding: ${spacing.base} 0;

  @media only screen and (max-width: 700px) {
    & #${EXAMPLE_CARD_ID} {
      display: none;
    }
  }
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: ${spacing.plusHalf};
  flex: 1 0 0;
  max-width: 400px;

  ${Body2} {
    color: ${colors.textSecondary};
  }
`;

const GoalButton = ({
  onClick,
  buttonText,
  innerRef,
}: {
  onClick: () => void;
  buttonText: string;
  innerRef?: React.ForwardedRef<HTMLButtonElement>;
}) => (
  <Button
    ref={innerRef}
    onClick={onClick}
    size={BUTTON_SIZES.LARGE}
    variant={BUTTON_VARIANTS.SECONDARY}
    data-test="create-goal-button"
  >
    <AddNew24 /> {buttonText}
  </Button>
);

const getTrigger = (buttonText: string) =>
  forwardRef<HTMLButtonElement, SelectValueDisplayProps<SelectValueType>>(
    ({ toggleDropdown }, ref) => (
      <GoalButton onClick={() => toggleDropdown(true)} buttonText={buttonText} innerRef={ref} />
    ),
  );

type GoalsDashboardEmptyStateProps = {
  canCreateGoals: boolean;
  hasGoalsInProject: boolean;
};

export const GoalsDashboardEmptyState = ({
  canCreateGoals = false,
  hasGoalsInProject = false,
}: GoalsDashboardEmptyStateProps) => {
  const { openModal, closeModal } = useModalContext();

  const openCreateGoalModal = () =>
    openModal(ModalType.CreateGoalModal, {
      onClose: () => closeModal(ModalType.CreateGoalModal),
    });

  const { title, body, button } = match({
    canCreateGoals,
    hasGoalsInProject,
  })
    .with({ canCreateGoals: false, hasGoalsInProject: false }, () => ({
      title: 'Unlock your growth',
      body: 'Ask a teammate with permissions to create a goal so you can start measuring impact.',
      button: null,
    }))
    .with({ canCreateGoals: true, hasGoalsInProject: false }, () => ({
      title: 'Unlock your growth',
      body: 'Track conversions, monitor campaign engagement, and visualize progress towards critical milestones.',
      button: <GoalButton buttonText="Create goal" onClick={openCreateGoalModal} />,
    }))
    .otherwise(() => ({
      title: 'Unlock your growth',
      body: "Create a goal to start measuring impact—or add one that's already set up.",
      button: (
        <AddGoalSelect
          onSelect={noop} // not used
          Trigger={getTrigger('Add goal')}
          verticalAlignment={ALIGNMENT.UP}
          horizontalAlignment={ALIGNMENT.LEFT}
        />
      ),
    }));

  return (
    <FlexSpacer justifyContent="center" alignItems="center" width="100%">
      <GoalsDashboardEmptyStateContent data-test="goals-empty-state">
        <EmptyStateExampleCard id={EXAMPLE_CARD_ID} data-test="empty-state-example-card" />

        <ContentWrapper>
          <span>
            <Subtitle1 data-test="empty-state-title">{title}</Subtitle1>
            <Body2 data-test="empty-state-body">{body}</Body2>
          </span>
          {button}
        </ContentWrapper>
      </GoalsDashboardEmptyStateContent>
    </FlexSpacer>
  );
};

export const GoalsDashboardDataState = ({ goals }: { goals: Goal[] }) => {
  return (
    <DashboardSectionContent data-test="goals-data-state">
      {goals.map((goal, index) => (
        <GoalCard goal={goal} key={index} isLastCard={index + 1 === goals.length} />
      ))}
    </DashboardSectionContent>
  );
};
